/**
 * StatsWidget Component
 *
 * Unified component for displaying statistics across all tools.
 * Supports multiple variants: grid, bar, toolbar, and compact
 */

import React from "react";
import PropTypes from "prop-types";
import "./StatsWidget.scss";

const StatsWidget = ({
  stats = [],
  variant = "bar",
  showIcons = true,
  className = "",
  loading = false,
}) => {
  if (!stats || stats.length === 0) {
    return null;
  }

  const variantClass = `stats-widget-${variant}`;

  return (
    <div
      className={`stats-widget ${variantClass} ${className} ${
        loading ? "loading" : ""
      }`}
    >
      {variant === "toolbar" && (
        <div className="toolbar-stats">
          {stats.map((stat) => (
            <div key={stat.key} className="toolbar-stat-item">
              {showIcons && stat.icon && (
                <span
                  className={`stat-icon-toolbar${stat.iconClassName ? ` ${stat.iconClassName}` : ""}`}
                  style={{ color: stat.color }}
                >
                  {React.createElement(stat.icon)}
                </span>
              )}
              <div className="stat-content-toolbar">
                <div className="stat-label">{stat.label}</div>
                <div className="stat-number" style={{ color: stat.color }}>
                  {loading ? (
                    <span
                      className="stat-loading"
                      style={{ color: stat.color }}
                    >
                      <span className="loading-dot">•</span>
                      <span className="loading-dot">•</span>
                      <span className="loading-dot">•</span>
                    </span>
                  ) : (
                    stat.value
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {variant === "bar" && (
        <div className="bar-stats">
          {stats.map((stat) => (
            <div key={stat.key} className="bar-stat-item">
              {showIcons && stat.icon && (
                <span
                  className={`stat-icon-inline${stat.iconClassName ? ` ${stat.iconClassName}` : ""}`}
                  style={{ color: stat.color }}
                >
                  {React.createElement(stat.icon)}
                </span>
              )}
              <span className="stat-label">{stat.label}:</span>
              <span className="stat-value" style={{ color: stat.color }}>
                {loading ? (
                  <span className="stat-loading" style={{ color: stat.color }}>
                    <span className="loading-dot">•</span>
                    <span className="loading-dot">•</span>
                    <span className="loading-dot">•</span>
                  </span>
                ) : (
                  stat.value
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {variant === "grid" && (
        <div className="grid-stats">
          {stats.map((stat) => (
            <div key={stat.key} className="grid-stat-card">
              {showIcons && stat.icon && (
                <div
                  className={`stat-icon${stat.iconClassName ? ` ${stat.iconClassName}` : ""}`}
                  style={{ backgroundColor: stat.iconColor || stat.color }}
                >
                  {React.createElement(stat.icon)}
                </div>
              )}
              <div className="stat-content">
                <div className="stat-label">{stat.label}</div>
                <div
                  className={`stat-value ${loading ? "loading-shimmer" : ""}`}
                  style={{ color: loading ? "#9ca3af" : stat.color }}
                >
                  {loading ? "—" : stat.value}
                </div>
                {stat.subtext && (
                  <div className="stat-subtext">{stat.subtext}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {variant === "compact" && (
        <div className="compact-stats">
          {stats.map((stat) => (
            <div key={stat.key} className="compact-stat-item">
              {stat.icon && (
                <span
                  className={`stat-icon-small${stat.iconClassName ? ` ${stat.iconClassName}` : ""}`}
                  style={{ color: stat.color }}
                >
                  {React.createElement(stat.icon)}
                </span>
              )}
              <span
                className={`stat-value ${loading ? "loading-shimmer" : ""}`}
                style={{ color: loading ? "#9ca3af" : stat.color }}
              >
                {loading ? "—" : stat.value}
              </span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

StatsWidget.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icon: PropTypes.elementType,
      color: PropTypes.string,
      iconColor: PropTypes.string,
      iconClassName: PropTypes.string,
      subtext: PropTypes.string,
    })
  ),
  variant: PropTypes.oneOf(["grid", "bar", "toolbar", "compact"]),
  showIcons: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
};

export default StatsWidget;
